/**
 * Modal final com resumo e link de compartilhamento
 * Exibida após completar o onboarding com sucesso
 */

import {
  Box,
  Modal,
  Paper,
  Typography,
  Button,
  Stack,
  IconButton,
} from '@mui/material';
import { useState } from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import { authService } from '../../services/authService';

export default function ShareLinkModal({ onboarding, result, createdIds }) {
  const [copied, setCopied] = useState(false);

  const publicLink = `https://share.agendario.com.br/schedule/${createdIds?.scheduleId || ''}`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(publicLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getThemeStorageKey = () => {
    const userId = authService.getUserInfo()?.userId;
    return userId ? `agendario_theme_${userId}` : 'agendario_theme';
  };

  const handleCloseModal = () => {
    onboarding.completeOnboarding();
    if (onboarding.theme) {
      localStorage.setItem(getThemeStorageKey(), onboarding.theme);
    }
    window.location.href = '/dashboard';
  };

  const handleGoToDashboard = () => {
    onboarding.completeOnboarding();
    if (onboarding.theme) {
      localStorage.setItem(getThemeStorageKey(), onboarding.theme);
    }
    window.location.href = '/dashboard';
  };

  // Build descriptive message
  const getDescriptiveMessage = () => {
    const profName = result?.professional?.name || 'profissional';
    const serviceName = result?.service?.name || 'serviço';
    const placeName = result?.place?.name || 'local';
    const scheduleName = result?.schedule?.name || 'agenda';

    return (
      <>
        Criamos para você o(a) profissional <strong>{profName}</strong>, com o serviço "
        <strong>{serviceName}</strong>", configurado no <strong>{placeName}</strong> com a{' '}
        <strong>{scheduleName}</strong> já funcional pra todos os dias da semana. Quer adicionar
        mais profissionais, serviços ou editar tudo isso? Acessa o painel de admin! Mas antes,
        compartilha esse link com seus clientes e deixa eles agendarem direto! 🚀
      </>
    );
  };

  return (
    <Modal
      open={true}
      onClose={handleCloseModal}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0, 0, 0, 0.5)',
      }}
    >
      <Paper
        elevation={8}
        sx={{
          maxWidth: '700px',
          width: '90%',
          borderRadius: 2,
          position: 'relative',
        }}
      >
        {/* Close Button */}
        <IconButton
          onClick={handleCloseModal}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1,
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Content */}
        <Box sx={{ p: 3 }}>
          {/* Success Header */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <CheckCircleIcon
              sx={{
                fontSize: '3rem',
                color: '#4caf50',
                mb: 1,
              }}
            />
            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 0.5 }}>
              Parabéns! 🎉
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Tudo pronto para começar a receber agendamentos
            </Typography>
          </Box>

          {/* Descriptive Message */}
          <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#333' }}>
              {getDescriptiveMessage()}
            </Typography>
          </Box>

          {/* Public Link Section - Compact */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: '#ffffff',
              p: 2,
              borderRadius: 1,
              mb: 3,
            }}
          >
            <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 500 }}>
              ✨ Link de Agendamento (já está ao vivo!)
            </Typography>
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                padding: '8px 12px',
                borderRadius: 1,
                alignItems: 'center',
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  fontFamily: 'monospace',
                  wordBreak: 'break-all',
                  flex: 1,
                  opacity: 0.95,
                }}
              >
                {publicLink}
              </Typography>
              <IconButton
                onClick={handleCopyLink}
                sx={{ color: '#ffffff', p: 0.5 }}
                size="small"
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Box>
            {copied && (
              <Typography variant="caption" sx={{ mt: 1, display: 'block', color: '#c8e6c9' }}>
                ✓ Copiado com sucesso!
              </Typography>
            )}
          </Box>

          {/* Action Buttons */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Button
              onClick={handleCopyLink}
              variant="contained"
              fullWidth
              sx={{
                py: 1,
                fontWeight: 600,
                backgroundColor: '#667eea',
                '&:hover': {
                  backgroundColor: '#5568d3',
                },
              }}
            >
              📋 Copiar Link
            </Button>

            <Button
              onClick={handleGoToDashboard}
              variant="contained"
              fullWidth
              sx={{
                py: 1,
                fontWeight: 600,
                backgroundColor: '#4caf50',
                '&:hover': {
                  backgroundColor: '#45a049',
                },
              }}
            >
              ⚙️ Ir para Admin
            </Button>
          </Stack>
        </Box>
      </Paper>
    </Modal>
  );
}
